import { DatePicker, Grid, GridColumn, GridRow, Tab, TabItemProps } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { AbsenceEvaluationMemberSummary } from 'components/Absences/components/AbsenceEvaluationMemberSummary';
import { AbsenceEvaluationMemberOverviewTable } from 'components/Absences/tables/AbsenceEvaluationMemberOverviewTable';
import { BpPage } from 'components/BpPage/BpPage';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import dayjs from 'dayjs';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from 'urql';

const AbsencesEvaluationClassMemberSubpageQuery = graphql(`
  query AbsencesEvaluationClassMemberSubpageData(
    $classesWhere: ClassWhere
    $profilesWhere: ProfileWhere
    $from: Date!
    $to: Date!
    $memberUuid: ID!
  ) {
    classes(where: $classesWhere) {
      __typename
      uuid
      name
    }
    profiles(where: $profilesWhere) {
      __typename
      uuid
      selectName
    }
    ...AbsenceEvaluationMemberSummaryFields
  }
`);

export const AbsencesEvaluationClassMemberSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organization = pimAuthClaims.getOrganization();
  const { currentSchoolYear } = useContext(OrganizationConfigContext);
  const { classUuid, memberUuid } = useParams();

  const startDate = dayjs(currentSchoolYear.start).toDate();
  const endDate = dayjs(currentSchoolYear.end).toDate();

  const [fromDate, setFromDate] = useState<Date>(startDate);
  const [toDate, setToDate] = useState<Date>(endDate);

  const [{ data }] = useQuery({
    query: AbsencesEvaluationClassMemberSubpageQuery,
    variables: {
      memberUuid: memberUuid ?? '',
      from: fromDate,
      to: toDate,
      classesWhere: {
        uuid: classUuid,
        organization: { uuid: organization.uuid },
      },
      profilesWhere: {
        uuid: memberUuid,
        organization: { uuid: organization.uuid },
      },
    },
    pause: !classUuid || !memberUuid,
  });

  const selectedClass = data?.classes[0] ?? null;
  const student = data?.profiles[0] ?? null;

  const permissions = usePermissionChecker();

  const tabs: TabItemProps[] = [
    {
      title: t('common.summary'),
      value: 'summary',
      content: <AbsenceEvaluationMemberSummary query={data} />,
    },
    {
      title: t('absences.evaluation.allEntries'),
      value: 'overview',
      content: <AbsenceEvaluationMemberOverviewTable />,
    },
  ];

  return (
    <BpPage
      title={student?.selectName ?? ''}
      subheadline={selectedClass?.name ?? ''}
      isForbidden={!permissions?.canViewAbsencesEvaluation()}
      showBackButton
    >
      <Grid useFormGap className='mb-4'>
        <GridRow>
          <GridColumn width={2}>
            <DatePicker
              onChange={(value) => setFromDate(value ?? new Date())}
              name='from'
              label={t('common.start')}
              value={fromDate}
              maxDate={toDate}
            />
          </GridColumn>
          <GridColumn width={2}>
            <DatePicker
              onChange={(value) => setToDate(value ?? new Date())}
              name='to'
              label={t('common.end')}
              value={toDate}
              minDate={fromDate}
            />
          </GridColumn>
        </GridRow>
      </Grid>

      <Tab tabs={tabs} />
    </BpPage>
  );
};
